/**
 * Room-scale “find the fault” circuit model — pure TypeScript (no Three.js).
 *
 * Not a SPICE solver. A device is powered iff its panel output shares an
 * undirected jumper path with the source bus (`srcP`) AND its switch (if any)
 * is closed. Missing jumper = off. Switch ON with no jumper = still off.
 *
 * Test points: {@link nodeHasVoltage} / {@link meterReading} treat `srcP` as
 * always live; panel-output voltage does not flow through branch switches.
 * {@link SWITCH_LOAD_TEST_NODE_ID} is the conceptual load side of LIGHT B.
 * Continuity is undirected jumper path only ({@link pathExists}).
 *
 * Environment / hands must use the stable ids below for jumpers, switches,
 * and device lookups.
 */

import { CONCEPT_VOLTAGE_V } from "./ohmsLaw";

/** Source positive bus — jumpers plug here. */
export const ROOM_SOURCE_BUS_ID = "srcP" as const;

/**
 * Conceptual load-side of the LIGHT B wall switch.
 * Live only when {@link ROOM_DEVICES} `ceilingLightB` is powered (jumper path
 * AND switch closed). The line-side jack uses panel node `outLightB`
 * (jumper voltage; the switch does not affect it).
 */
export const SWITCH_LOAD_TEST_NODE_ID = "loadLightB" as const;

/**
 * Placement hint for hands: the source bus accepts this many plugs so every
 * output can be jumpered at once without daisy-chaining.
 */
export const ROOM_SOURCE_BUS_MAX_OCCUPANTS = 5;

export const ROOM_OUTPUT_IDS = [
  "outLightA",
  "outLightB",
  "outSign",
  "outFan",
  "outOutlet",
] as const;

export type RoomOutputId = (typeof ROOM_OUTPUT_IDS)[number];

export const ROOM_PANEL_NODE_IDS = [
  ROOM_SOURCE_BUS_ID,
  ...ROOM_OUTPUT_IDS,
] as const;

export type RoomPanelNodeId = (typeof ROOM_PANEL_NODE_IDS)[number];

export const ROOM_DEVICE_IDS = [
  "ceilingLightA",
  "ceilingLightB",
  "sign",
  "fan",
  "outlet",
] as const;

export type RoomDeviceId = (typeof ROOM_DEVICE_IDS)[number];

export const ROOM_SWITCH_IDS = ["switchLightB"] as const;

export type RoomSwitchId = (typeof ROOM_SWITCH_IDS)[number];

/** Wall / branch switch that gates a device after its output is jumpered. */
export interface RoomSwitchSpec {
  readonly id: RoomSwitchId;
  /** Healthy baseline: closed = ON. */
  readonly defaultClosed: boolean;
}

export const ROOM_SWITCHES: readonly RoomSwitchSpec[] = [
  { id: "switchLightB", defaultClosed: true },
];

export interface RoomDeviceSpec {
  readonly id: RoomDeviceId;
  readonly outputId: RoomOutputId;
  /** If set, device is powered only when this switch is closed. */
  readonly switchId?: RoomSwitchId;
}

export const ROOM_DEVICES: readonly RoomDeviceSpec[] = [
  { id: "ceilingLightA", outputId: "outLightA" },
  { id: "ceilingLightB", outputId: "outLightB", switchId: "switchLightB" },
  { id: "sign", outputId: "outSign" },
  { id: "fan", outputId: "outFan" },
  { id: "outlet", outputId: "outOutlet" },
];

export const ROOM_SOURCE = {
  id: ROOM_SOURCE_BUS_ID,
  maxOccupants: ROOM_SOURCE_BUS_MAX_OCCUPANTS,
} as const;

/**
 * Healthy baseline jumpers: `srcP` connected to every output.
 * Hands seating should start from this list, then omit the active fault’s open.
 */
export const HEALTHY_ROOM_CONNECTIONS: ReadonlyArray<readonly [string, string]> =
  ROOM_OUTPUT_IDS.map((outputId) => [ROOM_SOURCE_BUS_ID, outputId] as const);

/** Healthy baseline: Light B switch ON. Other devices have no switch. */
export const HEALTHY_ROOM_SWITCHES: Readonly<Record<RoomSwitchId, boolean>> = {
  switchLightB: true,
};

/**
 * Legacy Light A–only sandbox (1 jumper, switch OFF). Prefer authored faults
 * via `roomFaults.ts`; kept for {@link evaluateInitialRoomCircuit}.
 */
export const INITIAL_ROOM_CONNECTIONS: ReadonlyArray<readonly [string, string]> =
  [[ROOM_SOURCE_BUS_ID, "outLightA"]];

export const INITIAL_ROOM_SWITCHES: Readonly<Record<RoomSwitchId, boolean>> = {
  switchLightB: false,
};

/** Voltage / continuity probe result. Conceptual 12 V source; not SPICE. */
export interface MeterVoltageReading {
  readonly kind: "voltage";
  readonly volts: number;
}

export type ContinuityDisplay = "CONT" | "OPEN";

const PANEL_NODE_SET: ReadonlySet<string> = new Set(ROOM_PANEL_NODE_IDS);
const DEVICE_ID_SET: ReadonlySet<string> = new Set(ROOM_DEVICE_IDS);
const SWITCH_ID_SET: ReadonlySet<string> = new Set(ROOM_SWITCH_IDS);
const DEVICE_BY_ID: ReadonlyMap<RoomDeviceId, RoomDeviceSpec> = new Map(
  ROOM_DEVICES.map((d) => [d.id, d]),
);

export function isRoomPanelNodeId(id: string): id is RoomPanelNodeId {
  return PANEL_NODE_SET.has(id);
}

export function isRoomDeviceId(id: string): id is RoomDeviceId {
  return DEVICE_ID_SET.has(id);
}

export function isRoomSwitchId(id: string): id is RoomSwitchId {
  return SWITCH_ID_SET.has(id);
}

export function isRoomOutputId(id: string): id is RoomOutputId {
  return id !== ROOM_SOURCE_BUS_ID && PANEL_NODE_SET.has(id);
}

/** Undirected jumper lead — two panel (or unknown) endpoints. */
export type RoomConnection = [string, string];

export interface RoomCircuitInput {
  readonly connections: ReadonlyArray<readonly [string, string]>;
  readonly switches: Readonly<Record<string, boolean>>;
}

export interface RoomCircuitState {
  /** Snapshot: true iff that device is currently powered. */
  readonly powered: Readonly<Record<RoomDeviceId, boolean>>;
  readonly poweredCount: number;
  readonly totalCount: number;
  /**
   * Panel nodes sharing an undirected jumper path with `srcP` (always includes
   * `srcP`). Cached at evaluate so meter probes can test live without rebuilding
   * the adjacency graph.
   */
  readonly liveNodes: ReadonlySet<string>;
  isPowered(deviceId: string): boolean;
}

function emptyPowered(): Record<RoomDeviceId, boolean> {
  return {
    ceilingLightA: false,
    ceilingLightB: false,
    sign: false,
    fan: false,
    outlet: false,
  };
}

function addUndirected(
  adj: Map<string, Set<string>>,
  a: string,
  b: string,
): void {
  if (a === b) return;
  let fromA = adj.get(a);
  if (!fromA) {
    fromA = new Set();
    adj.set(a, fromA);
  }
  fromA.add(b);
  let fromB = adj.get(b);
  if (!fromB) {
    fromB = new Set();
    adj.set(b, fromB);
  }
  fromB.add(a);
}

function adjacencyFromConnections(
  connections: ReadonlyArray<readonly [string, string]>,
): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  for (const pair of connections) {
    const a = pair[0];
    const b = pair[1];
    if (typeof a !== "string" || typeof b !== "string") continue;
    if (a.length === 0 || b.length === 0) continue;
    addUndirected(adj, a, b);
  }
  return adj;
}

function nodesReachableFrom(
  start: string,
  adj: Map<string, Set<string>>,
): Set<string> {
  const reachable = new Set<string>([start]);
  const queue: string[] = [start];
  for (let i = 0; i < queue.length; i++) {
    const cur = queue[i]!;
    for (const next of adj.get(cur) ?? []) {
      if (reachable.has(next)) continue;
      reachable.add(next);
      queue.push(next);
    }
  }
  return reachable;
}

/** Nodes that share an undirected jumper path with the source bus. */
function nodesReachableFromSource(
  connections: ReadonlyArray<readonly [string, string]>,
): Set<string> {
  return nodesReachableFrom(
    ROOM_SOURCE_BUS_ID,
    adjacencyFromConnections(connections),
  );
}

function connectionsOf(
  input: RoomCircuitInput | ReadonlyArray<readonly [string, string]>,
): ReadonlyArray<readonly [string, string]> {
  return isCircuitInput(input) ? input.connections : input;
}

function isCircuitInput(
  input: RoomCircuitInput | ReadonlyArray<readonly [string, string]>,
): input is RoomCircuitInput {
  return (
    typeof input === "object" &&
    input !== null &&
    !Array.isArray(input) &&
    "connections" in input &&
    "switches" in input
  );
}

function asCircuitInput(
  input: RoomCircuitInput | ReadonlyArray<readonly [string, string]>,
): RoomCircuitInput {
  if (isCircuitInput(input)) return input;
  return { connections: input, switches: {} };
}

/**
 * True if a probe at `nodeId` should read voltage.
 *
 * - `srcP` is always live.
 * - Panel outputs are live if they share an undirected jumper path with `srcP`.
 *   Branch switches do not affect those panel-node readings.
 * - {@link SWITCH_LOAD_TEST_NODE_ID} is live only when Light B is powered
 *   (jumper path AND switch closed) — the conceptual load side of the switch.
 */
export function nodeHasVoltage(
  nodeId: string,
  input: RoomCircuitInput | ReadonlyArray<readonly [string, string]>,
): boolean {
  if (typeof nodeId !== "string" || nodeId.length === 0) return false;
  if (nodeId === SWITCH_LOAD_TEST_NODE_ID) {
    return evaluateRoomCircuit(asCircuitInput(input)).isPowered("ceilingLightB");
  }
  if (nodeId === ROOM_SOURCE_BUS_ID) return true;
  return nodesReachableFromSource(connectionsOf(input)).has(nodeId);
}

/**
 * Undirected jumper continuity between two nodes (does not require `srcP`).
 * A node is continuous with itself. Empty ids are not.
 */
export function pathExists(
  a: string,
  b: string,
  input: RoomCircuitInput | ReadonlyArray<readonly [string, string]>,
): boolean {
  if (typeof a !== "string" || typeof b !== "string") return false;
  if (a.length === 0 || b.length === 0) return false;
  if (a === b) return true;
  const reachable = nodesReachableFrom(a, adjacencyFromConnections(connectionsOf(input)));
  return reachable.has(b);
}

/**
 * Conceptual voltage at a panel node: {@link CONCEPT_VOLTAGE_V} if live,
 * otherwise 0 V.
 */
export function meterReading(
  nodeId: string,
  input: RoomCircuitInput | ReadonlyArray<readonly [string, string]>,
): MeterVoltageReading {
  return {
    kind: "voltage",
    volts: nodeHasVoltage(nodeId, input) ? CONCEPT_VOLTAGE_V : 0,
  };
}

const VOLTAGE_DISPLAY_LIVE = `${CONCEPT_VOLTAGE_V.toFixed(1)} V`;
const VOLTAGE_DISPLAY_OPEN = "0.0 V";

/** `"12.0 V"` / `"0.0 V"` (one decimal, conceptual). Interned for the two concept values. */
export function formatVoltageDisplay(
  reading: MeterVoltageReading | number,
): string {
  const volts = typeof reading === "number" ? reading : reading.volts;
  if (volts === CONCEPT_VOLTAGE_V) return VOLTAGE_DISPLAY_LIVE;
  if (volts === 0) return VOLTAGE_DISPLAY_OPEN;
  return `${volts.toFixed(1)} V`;
}

/** `"CONT"` if a jumper path exists, otherwise `"OPEN"`. */
export function continuityDisplay(
  a: string,
  b: string,
  input: RoomCircuitInput | ReadonlyArray<readonly [string, string]>,
): ContinuityDisplay {
  return pathExists(a, b, input) ? "CONT" : "OPEN";
}

function switchIsClosed(
  switches: Readonly<Record<string, boolean>>,
  switchId: RoomSwitchId,
): boolean {
  return switches[switchId] === true;
}

/**
 * Evaluate jumper + switch state for the room panel.
 *
 * Duplicate jumpers and self-loops are ignored. Endpoint order does not
 * matter. Unknown node ids still participate in the jumper graph (so a path
 * through an extra terminal can energize an output) but only {@link ROOM_DEVICES}
 * appear in {@link RoomCircuitState.powered}.
 */
export function evaluateRoomCircuit(input: RoomCircuitInput): RoomCircuitState {
  const reachable = nodesReachableFromSource(input.connections);
  const powered = emptyPowered();

  for (const device of ROOM_DEVICES) {
    const jumpered = reachable.has(device.outputId);
    const gatedOk = device.switchId
      ? switchIsClosed(input.switches, device.switchId)
      : true;
    powered[device.id] = jumpered && gatedOk;
  }

  let poweredCount = 0;
  for (const id of ROOM_DEVICE_IDS) {
    if (powered[id]) poweredCount += 1;
  }

  return {
    powered,
    poweredCount,
    totalCount: ROOM_DEVICE_IDS.length,
    liveNodes: reachable,
    isPowered(deviceId: string): boolean {
      if (!isRoomDeviceId(deviceId)) return false;
      return powered[deviceId];
    },
  };
}

/** Healthy baseline: all five devices powered, Light B switch ON. */
export function evaluateHealthyRoomCircuit(): RoomCircuitState {
  return evaluateRoomCircuit({
    connections: HEALTHY_ROOM_CONNECTIONS,
    switches: HEALTHY_ROOM_SWITCHES,
  });
}

/** Legacy sandbox: Light A jumpered and on; 1 of 5 devices powered. */
export function evaluateInitialRoomCircuit(): RoomCircuitState {
  return evaluateRoomCircuit({
    connections: INITIAL_ROOM_CONNECTIONS,
    switches: INITIAL_ROOM_SWITCHES,
  });
}

export function getRoomDeviceSpec(
  deviceId: string,
): RoomDeviceSpec | undefined {
  if (!isRoomDeviceId(deviceId)) return undefined;
  return DEVICE_BY_ID.get(deviceId);
}
